import { useCallback, useEffect, useState } from "react";
import { apiRequest } from "./apiRequest";
import { showToast } from "./toastOptions";

/**
 * Hook to fetch company_permission_template from backend session bundle.
 * Templates are NOT stored client-side - they're fetched on-demand from the backend.
 * This prevents users from viewing or editing templates via DevTools.
 *
 * @param {Object} options
 * @param {boolean} options.includeUser - also return the user object from the same response
 * @returns {Object} { template, loading, error, refresh, user? }
 */
export function usePermissionTemplate({ includeUser = false } = {}) {
  const [template, setTemplate] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const json = await apiRequest(
        `${process.env.REACT_APP_API_BASE_URL}/get-user-info/?include_permission_template=1`,
        { method: "GET", suppressErrorToast: true }
      );
      const templateData = json?.data?.company_permission_template || null;
      setTemplate(templateData);
      if (includeUser) {
        setUser(json?.data || null);
      }
    } catch (err) {
      setTemplate(null);
      if (includeUser) setUser(null);
      setError(err.message || "Failed to fetch permission template");
    } finally {
      setLoading(false);
    }
  }, [includeUser]);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  useEffect(() => {
    if (error) {
      showToast("error", typeof error === "string" ? error : "Failed to fetch permission template");
    }
  }, [error]);

  let result = {
    template,
    loading,
    error,
    refresh: fetchTemplate,
  };

  if (includeUser) {
    result.user = user;
  }

  return result;
}
